import { createReducer } from '@reduxjs/toolkit';
import { AsyncStatus } from 'lib-shared-types-api';

import { suggestionActions } from '../elevio.actions/suggestions';
import { ArticleSuggestion } from '../types';
import {
  ElevioLanguage,
  ElevioLanguages,
  SelectedLanguageMode,
} from '../types/elevio.actions';

export type SelectedLanguage = {
  mode: SelectedLanguageMode;
} & ElevioLanguage;
type AvailableLanguages = {
  loading: boolean;
  results: ElevioLanguages | undefined;
  error: Error | undefined;
};
type DetectedLanguages = Record<number, string>; // eg: [conversationId]: "langCode"

/** Elevio Languages in Redux store */
export type ElevioLanguageStore = {
  defaultLanguage: ElevioLanguage;
  /** Languages available/active in the Elevio Workspace */
  availableLanguages: AvailableLanguages;
  /** Languages detected on a per-conversation basis */
  detectedLanguages: DetectedLanguages;
};
type ElevioLanguageStoreSelectors = {
  selectedLanguage: SelectedLanguage;
  /** The language id stored in localStorage */
  selectedLanguageId: string | undefined;
  /** The language mode stored in localStorage */
  selectedLanguageMode: SelectedLanguageMode | undefined;
};

/** Combined LanguageStore and LanguageStoreSelectors */
export type ElevioCombinedLanguageStore = ElevioLanguageStore &
  ElevioLanguageStoreSelectors;

type SubscriptionStatus = 'Initialising' | 'Active' | 'Inactive';

/** Suggestion state for a given conversation */
type SuggestionState = {
  /** Array to support article interlinking */
  articleIds: ReadonlyArray<string>;
  /** Suggestions for conversation */
  suggestions: ArticleSuggestion[];
  /** UnableToFindTaggedSuggestions */
  unableToFindTaggedSuggestions: boolean;
};
/** Store of suggestion states on a per-conversation basis */
type ConversationStore = {
  [conversationId: number]: SuggestionState;
};

/** Store of data relating to Elevio suggestions */
export type ElevioSuggestionState = {
  /**
   * We don't want to store in localstorage because we have to check again
   * periodically just in case their token status changed
   */
  token: string | null;
  /** Elevio company uid */
  companyUid: string;
  languages: ElevioLanguageStore;
  /** The Auth stage when accessing an elevio token using a dixa token */
  authState: AsyncStatus;
  /** The subscription status of the elevio account */
  subscriptionStatus: SubscriptionStatus;
  /**
   * Suggestion state for each conversation - stores article being viewed on a
   * per-conversation basis
   */
  conversationStore: ConversationStore;
  /** Universal fetching state for suggestions */
  fetching: boolean;
  /** The Elevio user id */
  userId: number | null;
  /** The Elevio user id */
  userEmail: string | null;
  /** Error message handling */
  errorMessage: string | null;
};

export const initialSuggestionState: ElevioSuggestionState = {
  token: null,
  companyUid: '',
  authState: 'uninitialized',
  subscriptionStatus: 'Initialising',
  conversationStore: {},
  fetching: false,
  userId: null,
  userEmail: null,
  errorMessage: null,
  languages: {
    availableLanguages: {
      results: undefined,
      loading: false,
      error: undefined,
    },
    detectedLanguages: {},
    defaultLanguage: {
      id: '',
      nativeName: 'Loading...',
    },
  },
};

export const suggestionReducer = createReducer(
  initialSuggestionState,
  (builder) =>
    builder
      .addCase(
        suggestionActions.setInitialConversationSuggestionState,
        (state, action) => {
          state.conversationStore[action.payload.conversationId] = {
            articleIds: [],
            suggestions: [],
            unableToFindTaggedSuggestions: false,
          };
          state.errorMessage = null;
        },
      )
      .addCase(suggestionActions.fetchAvailableLanguagesRequest, (state) => {
        state.languages.availableLanguages.loading = true;
      })
      .addCase(
        suggestionActions.fetchAvailableLanguagesComplete,
        (state, action) => {
          const defaultLanguage = action.payload.languages.find(
            (lang) => lang.id === action.payload.defaultLanguageId,
          );
          if (defaultLanguage) {
            state.languages.defaultLanguage = defaultLanguage;
          }
          if (!state.languages.availableLanguages.results) {
            state.languages.availableLanguages = {
              loading: false,
              results: action.payload.languages,
              error: undefined,
            };
          }
        },
      )
      .addCase(suggestionActions.fetchAvailableLanguagesFailure, (state) => {
        state.languages.availableLanguages = {
          error: new Error('Unable to fetch available languages'),
          loading: false,
          results: state.languages.availableLanguages.results,
        };
        state.errorMessage =
          'Unable to fetch suggestions. Please try again or contact support if this issue persists.';
      })
      .addCase(suggestionActions.setDetectedLanguageId, (state, action) => {
        state.languages.detectedLanguages[action.payload.conversationId] =
          action.payload.languageId;
      })
      .addCase(suggestionActions.viewArticle, (state, action) => {
        const { articleId, ticketId } = action.payload;
        const conversationId = Number(ticketId);

        if (state.conversationStore[conversationId]) {
          state.conversationStore[conversationId].articleIds.push(articleId);
        }
      })
      .addCase(suggestionActions.articleNavBack, (state, action) => {
        const { conversationId } = action.payload;
        if (!state.conversationStore[conversationId]?.articleIds?.length) {
          return;
        }

        state.conversationStore[conversationId].articleIds =
          state.conversationStore[conversationId].articleIds.slice(0, -1);
      })
      .addCase(suggestionActions.clearTokenAndCompanyUid, (state) => {
        state.token = null;
        state.companyUid = '';
        state.authState = 'uninitialized';
        state.conversationStore = {};
        state.errorMessage = null;
      })
      .addCase(suggestionActions.fetchTokenPending, (state) => {
        state.authState = 'pending';
      })
      .addCase(suggestionActions.fetchTokenSuccess, (state, action) => {
        state.authState = 'fulfilled';
        state.token = action.payload.token;
        state.companyUid = action.payload.companyUid;
        state.userId = action.payload.userId;
        state.userEmail = action.payload.userEmail;
        state.errorMessage = null;
      })
      .addCase(suggestionActions.fetchTokenError, (state) => {
        state.authState = 'rejected';
        state.errorMessage =
          'Unable to authenticate your session. Please try again or contact support if this issue persists.';
      })
      .addCase(suggestionActions.subscriptionStatusSuccess, (state) => {
        state.subscriptionStatus = 'Active';
      })
      .addCase(suggestionActions.subscriptionStatusError, (state, action) => {
        state.subscriptionStatus = 'Inactive';
        if (action.payload.errorMessage) {
          state.errorMessage = action.payload.errorMessage;
        }
      })
      .addCase(suggestionActions.fetchSuggestionsRequest, (state) => {
        state.fetching = true;
      })
      .addCase(suggestionActions.fetchSuggestionsComplete, (state, action) => {
        state.fetching = false;
        state.errorMessage = null;

        if (state.conversationStore[action.payload.conversationId]) {
          state.conversationStore[action.payload.conversationId].suggestions =
            action.payload.results;
        }
      })
      .addCase(suggestionActions.fetchSuggestionsFailure, (state) => {
        state.fetching = false;
        state.errorMessage =
          'Unable to fetch suggestions. Please try again or contact support if this issue persists.';
      })
      .addCase(
        suggestionActions.setUnableToFindTaggedSuggestions,
        (state, action) => {
          const { conversationId } = action.payload;
          if (!state.conversationStore[conversationId]) {
            state.conversationStore[conversationId] = {
              articleIds: [],
              suggestions: [],
              unableToFindTaggedSuggestions: false,
            };
          }

          state.conversationStore[
            conversationId
          ].unableToFindTaggedSuggestions =
            action.payload.unableToFindTaggedSuggestions;
        },
      ),
);
